import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionsRow, Button, Icon } from '@vestigas/ui';

type ConfirmDialogProps = {
  readonly isOpen: boolean;
  readonly isConfirming?: boolean;
  readonly title: string;
  readonly content?: string | ReactNode;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly confirmText?: string;
  readonly testId?: string;
};

export function ConfirmDialog({
  isOpen,
  isConfirming = false,
  title,
  content,
  onConfirm,
  onCancel,
  confirmText,
  testId = 'confirm-dialog',
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');

  return (
    <Dialog
      aria-describedby="confirm-dialog-description"
      aria-labelledby="confirm-dialog-title"
      data-testid={testId}
      open={isOpen}
      onClose={isConfirming ? undefined : onCancel}
    >
      <header className="flex items-center justify-between gap-4 px-4">
        <DialogTitle
          id="confirm-dialog-title"
          className="min-w-0 truncate px-0 py-4"
        >
          {title}
        </DialogTitle>
        <Button
          buttonStyle="link"
          className="-mt-1 -mr-1 shrink-0 text-tiga-text-lighter hover:text-tiga-action-lighter"
          disabled={isConfirming}
          icon={<Icon metaphor="close" />}
          title={t(($) => $.actions.cancel)}
          onClick={onCancel}
        />
      </header>
      <DialogContent className="px-4 py-0">
        <DialogContentText
          id="confirm-dialog-description"
          className="text-sm text-tiga-text-default"
        >
          {content}
        </DialogContentText>
      </DialogContent>
      <DialogActions className="p-0">
        <ActionsRow
          primaryAction={{
            color: 'error',
            disabled: isConfirming,
            inProgress: isConfirming,
            onClick: onConfirm,
            text: confirmText ?? t(($) => $.actions.confirm),
          }}
          secondaryAction={{
            disabled: isConfirming,
            text: t(($) => $.actions.cancel),
          }}
          onCancel={onCancel}
        />
      </DialogActions>
    </Dialog>
  );
}
